import { Link } from "@tanstack/react-router";
import { MapPin, Syringe } from "lucide-react";
import { DownloadCta } from "@/components/landing/DownloadCta";
import { JsonLd } from "@/components/layout/JsonLd";
import { PageHero } from "@/components/layout/PageHero";
import { chrome } from "@/content/chrome";
import { pages } from "@/content/pages";
import { diseaseName } from "@/content/travel/diseases";
import type { Locale } from "@/i18n/locales";
import { localePath } from "@/i18n/locales";
import { canonicalFor, SITE_NAME, SITE_URL } from "@/lib/site";
import {
  countryName,
  fillTemplate,
  formatCrawlDate,
  generalVaccineNames,
  groupedRequirements,
  requiredVaccineNames,
  type TravelCountry,
} from "@/lib/travel-data";

type RegionCopy = (typeof pages)["nl"]["travel"]["regions"][number];

export type TravelSlugData =
  | {
      kind: "region";
      region: RegionCopy;
      countries: TravelCountry[];
    }
  | {
      kind: "country";
      country: TravelCountry;
      region?: RegionCopy;
      siblings: TravelCountry[];
    };

type TravelSlugViewProps = {
  locale: Locale;
  data: TravelSlugData;
};

const regionImages: Record<string, string> = {
  "zuidoost-azie": "/images/travel/southeast-asia.webp",
  "sub-sahara-afrika": "/images/travel/africa.webp",
  "zuid-amerika": "/images/travel/south-america.webp",
  "midden-oosten": "/images/travel/middle-east.webp",
  "europa-noord-amerika": "/images/travel/europe.webp",
  "zuid-centraal-azie": "/images/travel/south-asia.webp",
  cariben: "/images/travel/south-america.webp",
  "oost-azie": "/images/travel/east-asia.webp",
  oceanie: "/images/travel/destination.webp",
};

function joinNames(names: string[]): string {
  return names.join(", ");
}

export function countryFaqs(
  locale: Locale,
  country: TravelCountry,
): { q: string; a: string }[] {
  const copy = pages[locale].travel.destination;
  const name = countryName(country, locale);
  const recommended = joinNames(generalVaccineNames(country, locale)) || name;
  const required = requiredVaccineNames(country, locale);
  const vars = { name, vaccines: recommended, region: "" };
  return [
    {
      q: fillTemplate(copy.faqRequiredQ, vars),
      a: required.length
        ? fillTemplate(copy.faqRequiredYes, {
            ...vars,
            vaccines: joinNames(required),
          })
        : fillTemplate(copy.faqRequiredNo, vars),
    },
    {
      q: fillTemplate(copy.faqRecommendedQ, vars),
      a: fillTemplate(copy.faqRecommendedA, vars),
    },
    {
      q: copy.faqWhenQ,
      a: copy.faqWhenA,
    },
  ];
}

export function TravelSlugView({ locale, data }: TravelSlugViewProps) {
  return data.kind === "region" ? (
    <RegionPage
      locale={locale}
      region={data.region}
      countries={data.countries}
    />
  ) : (
    <CountryPage
      locale={locale}
      country={data.country}
      region={data.region}
      siblings={data.siblings}
    />
  );
}

function RegionPage({
  locale,
  region,
  countries,
}: {
  locale: Locale;
  region: RegionCopy;
  countries: TravelCountry[];
}) {
  const copy = pages[locale].travel;
  const dest = copy.destination;
  const title = fillTemplate(dest.title, { name: region.name });
  const path = `/travel/${region.slug}`;
  const faqs = [
    {
      q: fillTemplate(dest.faqRecommendedQ, { name: region.name }),
      a: `${copy.vaccineLabel}: ${region.vaccines.join(", ")}. ${region.note}`,
    },
    { q: dest.faqWhenQ, a: dest.faqWhenA },
  ];

  return (
    <main className="bg-background">
      <JsonLd
        data={{
          "@context": "https://schema.org",
          "@graph": [
            webPageNode({
              locale,
              path,
              title,
              description: region.note,
            }),
            faqNode(faqs),
            breadcrumbNode(locale, [
              { name: chrome[locale].nav.links.travel, path: "/travel" },
              { name: region.name, path },
            ]),
          ],
        }}
      />
      <PageHero eyebrow={copy.eyebrow} title={title} intro={region.note}>
        <img
          src={regionImages[region.slug]}
          alt=""
          width={600}
          height={400}
          loading="lazy"
          className="mx-auto mt-10 h-48 w-full max-w-xl rounded-2xl object-cover"
        />
      </PageHero>

      <section className="pb-16">
        <div className="mx-auto max-w-5xl px-5">
          <Breadcrumbs
            locale={locale}
            items={[
              { label: chrome[locale].nav.links.travel, href: "/travel" },
              { label: region.name },
            ]}
          />
          <h2 className="mb-4 font-heading text-2xl font-bold text-[#1a3a5c]">
            {copy.vaccineLabel}
          </h2>
          <ul className="mb-10 space-y-1.5">
            {region.vaccines.map((vaccine) => (
              <li
                key={vaccine}
                className="flex items-center gap-2 text-foreground"
              >
                <Syringe className="h-4 w-4 flex-shrink-0 text-primary" />
                {vaccine}
              </li>
            ))}
          </ul>
          <h2 className="mb-6 font-heading text-2xl font-bold text-[#1a3a5c]">
            {fillTemplate(dest.countriesTitle, { region: region.name })}
          </h2>
          <CountryList locale={locale} countries={countries} />
        </div>
      </section>

      <FaqBlock locale={locale} faqs={faqs} />
      <DownloadCta
        locale={locale}
        title={pages[locale].travel.ctaTitle}
        text={pages[locale].travel.ctaText}
      />
    </main>
  );
}

function CountryPage({
  locale,
  country,
  region,
  siblings,
}: {
  locale: Locale;
  country: TravelCountry;
  region?: RegionCopy;
  siblings: TravelCountry[];
}) {
  const copy = pages[locale].travel;
  const dest = copy.destination;
  const name = countryName(country, locale);
  const vaccines = generalVaccineNames(country, locale);
  const title = fillTemplate(dest.title, { name });
  const lead = vaccines.length
    ? fillTemplate(dest.lead, { name, vaccines: joinNames(vaccines) })
    : fillTemplate(dest.leadEmpty, { name });
  const path = `/travel/${country.slug}`;
  const faqs = countryFaqs(locale, country);
  const groups = groupedRequirements(country);
  const crumbs = [
    { label: chrome[locale].nav.links.travel, href: "/travel" as const },
    ...(region
      ? [{ label: region.name, href: `/travel/${region.slug}` as const }]
      : []),
    { label: name },
  ];

  return (
    <main className="bg-background">
      <JsonLd
        data={{
          "@context": "https://schema.org",
          "@graph": [
            {
              ...webPageNode({
                locale,
                path,
                title,
                description: lead,
                dateModified: country.lastCrawled,
              }),
              about: {
                "@type": "Country",
                name,
                ...(country.iso
                  ? {
                      identifier: `https://www.iso.org/obp/ui/#iso:code:3166:${country.iso}`,
                    }
                  : {}),
              },
            },
            faqNode(faqs),
            breadcrumbNode(
              locale,
              crumbs.map((item) => ({
                name: item.label,
                path: "href" in item && item.href ? item.href : path,
              })),
            ),
          ],
        }}
      />
      <PageHero eyebrow={copy.eyebrow} title={title} intro={lead} />

      <section className="pb-16">
        <div className="mx-auto max-w-3xl px-5">
          <Breadcrumbs locale={locale} items={crumbs} />

          <div className="mt-10 space-y-10">
            {groups.map((group) => (
              <div key={group.section}>
                <h2 className="mb-4 font-heading text-2xl font-bold text-[#1a3a5c]">
                  {dest.sections[group.section]}
                </h2>
                <ul className="space-y-4">
                  {group.items.map((item) => (
                    <li
                      key={`${item.disease}-${item.section}-${item.appliesTo}`}
                      className="rounded-2xl border border-border bg-card p-5"
                    >
                      <h3 className="mb-2 flex items-center gap-2 font-heading font-semibold text-[#1a3a5c]">
                        <Syringe className="h-4 w-4 text-primary" />
                        <Link
                          to={localePath(
                            locale,
                            `/travel/diseases/${item.disease}`,
                          )}
                          className="hover:text-primary hover:underline"
                        >
                          {diseaseName(item.disease, locale)}
                        </Link>
                      </h3>
                      <p className="text-sm leading-relaxed text-muted-foreground">
                        {item.text[locale] || item.text.nl}
                      </p>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          <p className="mt-14 text-sm text-muted-foreground">
            {dest.source}.{" "}
            {fillTemplate(dest.updated, {
              date: formatCrawlDate(country.lastCrawled, locale),
            })}
            {" · "}
            <a
              href={country.lcrUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="font-medium text-primary underline-offset-2 hover:underline"
            >
              {dest.sourceLink}
            </a>
          </p>

          {siblings.length > 0 && region ? (
            <div className="mt-14">
              <h2 className="mb-6 font-heading text-2xl font-bold text-[#1a3a5c]">
                {fillTemplate(dest.siblingsTitle, { region: region.name })}
              </h2>
              <CountryList locale={locale} countries={siblings} />
            </div>
          ) : null}

          <div className="mt-10 flex flex-wrap gap-4 text-sm font-semibold">
            <Link
              to={localePath(locale, "/travel")}
              className="text-primary hover:underline"
            >
              {dest.browseAll}
            </Link>
            <Link
              to={localePath(locale, "/blog/travel-vaccinations-guide")}
              className="text-primary hover:underline"
            >
              {dest.relatedGuide}
            </Link>
            <Link
              to={localePath(locale, "/app")}
              className="text-primary hover:underline"
            >
              {dest.relatedApp}
            </Link>
          </div>
        </div>
      </section>

      <FaqBlock locale={locale} faqs={faqs} />
      <DownloadCta
        locale={locale}
        title={pages[locale].travel.ctaTitle}
        text={pages[locale].travel.ctaText}
      />
    </main>
  );
}

function CountryList({
  locale,
  countries,
}: {
  locale: Locale;
  countries: TravelCountry[];
}) {
  const sorted = [...countries].sort((a, b) =>
    countryName(a, locale).localeCompare(countryName(b, locale), locale),
  );
  return (
    <ul className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
      {sorted.map((country) => (
        <li key={country.slug}>
          <Link
            to={localePath(locale, `/travel/${country.slug}`)}
            className="flex items-center gap-2 rounded-xl border border-border bg-card px-4 py-3 text-sm font-medium text-[#1a3a5c] transition-colors hover:border-primary/40 hover:bg-primary/5"
          >
            <MapPin className="h-4 w-4 flex-shrink-0 text-primary" />
            {countryName(country, locale)}
          </Link>
        </li>
      ))}
    </ul>
  );
}

function Breadcrumbs({
  locale,
  items,
}: {
  locale: Locale;
  items: { label: string; href?: string }[];
}) {
  return (
    <nav aria-label="Breadcrumb" className="mb-8 text-sm">
      <ol className="flex flex-wrap items-center gap-2 text-muted-foreground">
        {items.map((item, index) => (
          <li key={item.label} className="flex items-center gap-2">
            {index > 0 ? <span aria-hidden="true">/</span> : null}
            {item.href ? (
              <Link
                to={localePath(locale, item.href)}
                className="hover:text-foreground"
              >
                {item.label}
              </Link>
            ) : (
              <span className="text-foreground">{item.label}</span>
            )}
          </li>
        ))}
      </ol>
    </nav>
  );
}

function FaqBlock({
  locale,
  faqs,
}: {
  locale: Locale;
  faqs: { q: string; a: string }[];
}) {
  return (
    <section className="pb-16">
      <div className="mx-auto max-w-3xl px-5">
        <h2 className="mb-6 font-heading text-2xl font-bold text-[#1a3a5c]">
          {pages[locale].travel.destination.faqTitle}
        </h2>
        <dl className="space-y-6">
          {faqs.map((item) => (
            <div key={item.q}>
              <dt className="font-heading font-semibold text-[#1a3a5c]">
                {item.q}
              </dt>
              <dd className="mt-2 text-sm leading-relaxed text-muted-foreground">
                {item.a}
              </dd>
            </div>
          ))}
        </dl>
      </div>
    </section>
  );
}

function webPageNode(opts: {
  locale: Locale;
  path: string;
  title: string;
  description: string;
  dateModified?: string;
}) {
  return {
    "@type": "WebPage",
    name: opts.title,
    description: opts.description,
    url: canonicalFor(opts.locale, opts.path),
    inLanguage: opts.locale,
    isPartOf: { "@type": "WebSite", name: SITE_NAME, url: SITE_URL },
    author: {
      "@type": "Person",
      name: "Dr. Marco Goeijenbier",
      jobTitle: pages[opts.locale].about.team[0].title,
    },
    publisher: {
      "@type": "Organization",
      name: SITE_NAME,
      logo: { "@type": "ImageObject", url: `${SITE_URL}/icon.png` },
    },
    ...(opts.dateModified ? { dateModified: opts.dateModified } : {}),
  };
}

function faqNode(faqs: { q: string; a: string }[]) {
  return {
    "@type": "FAQPage",
    mainEntity: faqs.map((item) => ({
      "@type": "Question",
      name: item.q,
      acceptedAnswer: { "@type": "Answer", text: item.a },
    })),
  };
}

function breadcrumbNode(
  locale: Locale,
  items: { name: string; path: string }[],
) {
  return {
    "@type": "BreadcrumbList",
    itemListElement: items.map((item, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: item.name,
      item: canonicalFor(locale, item.path),
    })),
  };
}
